/**
 * Blog post metadata only: no React, no post bodies. `gatsby-node.ts` imports this module to create one
 * real page per post (MDCI-933), so the lazy `BLOG_LOADERS` live in `./index` and stay out of the Node
 * build (their 21 `import()`s would drag JSX, `@opswat/ui` and image aliases along). App code keeps
 * importing from `utils/blog`, whose barrel re-exports everything here.
 */
import { generateSlug, sortBlogByDateDesc } from './blog-helpers';
import type { BlogPostMeta } from './types';

/** English title keys — used for stable URL slugs and tag extraction (language-independent). */
export const TK = {
    aiThreats: 'Defence Against AI Threats; How to Identify, Analyze, and Reduce Risks',
    cloudFiles: "Cloud Files at Risk: Here's How to Keep Them Protected",
    communitySafe: 'MetaDefender Cloud Community; A Safe Place for Malware Analysis & Collaboration',
    saferInternet: 'Building a Safer Internet Together: How You Can Contribute to MetaDefender Cloud Community',
    multiEngine: "The Rise of Multi-Engine Scanning: Why One Engine Isn't Enough Anymore",
    urlScanners: 'Free URL & Link Scanners: How to Detect Phishing Before You Click',
    fileScans100k: 'What +100k File Scans Reveal About Everyday File-Based Cyber Risks',
    fileTransit: 'Why File Security in Transit Is Essential for Modern Networks',
    storageCloud: 'Secure Your Storage Data with MetaDefender Storage Security Cloud; Demo Available',
    secureChromeEdgeDownloads: 'How to Secure Chrome and Edge Downloads Before Malware Reaches Your Device',
    chat3Creators: 'Chat3 Strengthens Platform Security for +1M Creators with OPSWAT',
    grafanaGhost: 'What GrafanaGhost Teaches Us About Blind Trust in AI-Driven Workflows',
    vtAlternative: 'MetaDefender Cloud™ as a Prevention-Focused VirusTotal Alternative',
    securePipelineStudio: 'How to Create and Configure Custom Workflows in MetaDefender Cloud™',
    staticVsDynamicAnalysis: "Static vs. Dynamic Analysis: How to Close the Malware Detection Gap Before It's Too Late",
    deepCdr: 'What is Content Disarm and Reconstruction (CDR)? How File Sanitization Prevents Zero-Day Threats',
    mastraNpm:
        'North Korean State Actor Compromises 140+ @mastra AI Framework Packages in Coordinated NPM Supply Chain Attack',
    searchLeak: 'How SearchLeak (CVE-2026-42824) Could Have Exposed Orgs to One-Click Email Theft',
    jadePuffer: 'Sysdig Researchers Document JadePuffer: First Fully Agentic Ransomware Attack Chain',
    aiAgentSandboxEscapes:
        'AI Agent Security in 2026: What the OpenAI–Hugging Face Breach and Claude Cowork’s SharedRoot Flaw Reveal',
    ancpiRansomware: 'ANCPI Ransomware Attack 2026: How ByteToBreach Compromised Romania’s Land Registry',
    fakeClaudeDesktop: 'Fake Claude Desktop Campaign Delivers SectopRAT Through Malvertising and DLL Sideloading',
} as const;

export const slugCloudFiles = generateSlug(TK.cloudFiles);
export const slugCommunitySafe = generateSlug(TK.communitySafe);
export const slugSaferInternet = generateSlug(TK.saferInternet);
export const slugMultiEngine = generateSlug(TK.multiEngine);
export const slugUrlScanners = generateSlug(TK.urlScanners);
export const slugFileScans100k = generateSlug(TK.fileScans100k);
export const slugFileTransit = generateSlug(TK.fileTransit);
export const slugStorageCloud = generateSlug(TK.storageCloud);
export const slugAiThreats = generateSlug(TK.aiThreats);
export const slugSecureChromeEdgeDownloads = generateSlug(TK.secureChromeEdgeDownloads);
export const slugChat3Creators = generateSlug(TK.chat3Creators);
export const slugGrafanaGhost = generateSlug(TK.grafanaGhost);
export const slugVtAlternative = generateSlug(TK.vtAlternative);
export const slugSecurePipelineStudio = generateSlug(TK.securePipelineStudio);
export const slugStaticVsDynamicAnalysis = generateSlug(TK.staticVsDynamicAnalysis);
// Custom SEO slugs — shorter than the generated title slug. Exported so the
// post loaders reuse the exact same value (single source of truth).
export const slugDeepCdr = 'content-disarm-reconstruction-cdr-file-security';
export const slugMastraNpm = 'mastra-npm-supply-chain-attack';
export const slugSearchLeak = 'searchleak-cve-2026-42824-microsoft-365-copilot';
export const slugJadePuffer = 'jadepuffer-agentic-ransomware-langflow-cve-2025-3248';
export const slugAiAgentSandboxEscapes = generateSlug(TK.aiAgentSandboxEscapes);
export const slugAncpiRansomware = 'ancpi-ransomware-attack-bytetobreach';
export const slugFakeClaudeDesktop = 'fake-claude-desktop-malware-sectoprat';

const BLOG_ENTRIES: BlogPostMeta[] = [
    {
        slug: slugCloudFiles,
        titleEn: TK.cloudFiles,
        date: 'August 20th 2025',
        coverImage: '/image-1.jpg',
        seoTitle: 'Cloud File Security: How to Protect Cloud Files | MetaDefender Cloud',
        seoDescription:
            'Learn why cloud files are at risk from malware, risky uploads, and hidden threats, and how to protect cloud storage with file scanning and sanitization.',
    },
    {
        slug: slugCommunitySafe,
        titleEn: TK.communitySafe,
        date: 'September 20th 2025',
        coverImage: '/image-2.jpg',
        seoTitle: 'Malware Analysis Community | MetaDefender Cloud',
        seoDescription:
            'Join MetaDefender Cloud Community to collaborate on malware analysis, threat intelligence, file reputation, sandbox analysis, and advanced threat detection.',
    },
    {
        slug: slugSaferInternet,
        titleEn: TK.saferInternet,
        date: 'November 5th 2025',
        coverImage: '/image-3.jpg',
        seoTitle: 'Build Safer Cyber Defense with Community Threat Intelligence | MetaDefender Cloud',
        seoDescription:
            'Learn how MetaDefender Cloud Community turns shared malware analysis, suspicious files, and threat intelligence into stronger protection for everyone.',
    },
    {
        slug: slugMultiEngine,
        titleEn: TK.multiEngine,
        date: 'December 17th 2025',
        coverImage: '/frame-4.png',
        seoTitle: 'Multi-Engine Malware Scanning | MetaDefender Cloud',
        seoDescription:
            'One antivirus engine can miss advanced threats. Multi-engine malware scanning improves file analysis, detection coverage, and threat prevention.',
    },
    {
        slug: slugUrlScanners,
        titleEn: TK.urlScanners,
        date: 'January 15th 2026',
        coverImage: '/image-4.jpg',
        seoTitle: 'Free URL Scanner for Phishing Detection | MetaDefender Cloud',
        seoDescription:
            'Check suspicious links before you click. Free URL scanners help detect phishing, malicious domains, risky websites, and web-based threats.',
    },
    {
        slug: slugFileScans100k,
        titleEn: TK.fileScans100k,
        date: 'February 16th 2026',
        coverImage: '/image-5.jpg',
        seoTitle: '100K File Scans and File-Based Cyber Risks | MetaDefender Cloud',
        seoDescription:
            'See what 100K file scans reveal about malware detections, suspicious files, risky uploads, and everyday file-based cyber threats.',
    },
    {
        slug: slugFileTransit,
        titleEn: TK.fileTransit,
        date: 'March 2nd 2026',
        coverImage: '/image-6.jpg',
        seoTitle: 'ICAP Cloud for File Security in Transit | MetaDefender Cloud',
        seoDescription:
            'Secure files in transit with MetaDefender ICAP Cloud. Inspect network file traffic, detect malware, sanitize content, and enforce allow/block policies.',
    },
    {
        slug: slugStorageCloud,
        titleEn: TK.storageCloud,
        date: 'March 25th 2026',
        coverImage: '/image-7.jpg',
        seoTitle: 'Storage Security Cloud for Malware Protection | MetaDefender Cloud',
        seoDescription:
            'MetaDefender Storage Security Cloud helps secure cloud storage across AWS, Azure, and GCP with malware scanning, Deep CDR, automated policies, and threat prevention.',
    },
    {
        slug: slugAiThreats,
        titleEn: TK.aiThreats,
        date: 'April 6th 2026',
        coverImage: '/image-8.jpg',
        seoTitle: 'AI Threat Detection & Risk Reduction | MetaDefender Cloud',
        seoDescription:
            'AI-driven threats can evade traditional defenses. Discover how threat analysis, malware detection, file security, and risk reduction help protect modern workflows.',
    },
    {
        slug: slugSecureChromeEdgeDownloads,
        titleEn: TK.secureChromeEdgeDownloads,
        date: 'April 24th 2026',
        coverImage: '/image-10.png',
        seoTitle: 'Malware Scanning Browser Extension | MetaDefender Cloud',
        seoDescription:
            'Scan Chrome and Edge downloads for malware with the MetaDefender Cloud browser extension. Detect suspicious files before they reach your device.',
    },
    {
        slug: slugChat3Creators,
        titleEn: TK.chat3Creators,
        date: 'April 29th 2026',
        coverImage: '/image-9.jpg',
        seoTitle: 'Chat3 & MetaDefender Cloud Partnership | 1M+ Creators Protected',
        seoDescription:
            'Through its partnership with MetaDefender Cloud, Chat3 protects 1M+ creators with malware scanning, file security, threat detection, and safer uploads.',
    },
    {
        slug: slugGrafanaGhost,
        titleEn: TK.grafanaGhost,
        date: 'May 15th 2026',
        coverImage: '/image-12.jpg',
        seoTitle: 'GrafanaGhost & AI Workflow Security Risks | MetaDefender Cloud',
        seoDescription:
            'GrafanaGhost shows how blind trust in AI-driven workflows can create security risks. Explore threat analysis, workflow security, and safer automation practices.',
    },
    {
        slug: slugVtAlternative,
        titleEn: TK.vtAlternative,
        date: 'June 2nd 2026',
        coverImage: '/virusTotal.png',
        seoTitle: 'VirusTotal Alternative for File Security | MetaDefender Cloud',
        seoDescription:
            'MetaDefender Cloud goes beyond malware detection with multiscanning, Deep CDR, sandbox analysis, private file scanning, and proactive threat prevention.',
    },
    {
        slug: slugSecurePipelineStudio,
        titleEn: TK.securePipelineStudio,
        date: 'June 8th 2026',
        coverImage: '/blog%2014.png',
        seoTitle: 'Custom File Security Workflows | MetaDefender Cloud',
        seoDescription:
            'Configure custom workflows in MetaDefender Cloud to automate file analysis, apply security policies, sanitize risky files, and block unwanted content.',
    },
    {
        slug: slugStaticVsDynamicAnalysis,
        titleEn: TK.staticVsDynamicAnalysis,
        date: 'June 15th 2026',
        coverImage: '/blog15.png',
    },
    {
        slug: slugDeepCdr,
        titleEn: TK.deepCdr,
        date: 'July 6th 2026',
        coverImage: '/images/blog/blog16-preview.png',
        seoTitle: 'Deep CDR™: File Security Beyond Detection',
        seoDescription:
            'See how content disarm and reconstruction (CDR) neutralizes file-borne malware and zero-days. Try Deep CDR™ for free.',
    },
    {
        slug: slugMastraNpm,
        titleEn: TK.mastraNpm,
        date: 'July 8th 2026',
        coverImage: '/images/blog/blog-attack-NPM.png',
        seoTitle: 'Mastra NPM Supply Chain Attack: 140+ AI Packages Compromised',
        seoDescription:
            'Learn how the Mastra npm supply chain attack compromised 140+ AI packages, exposed CI/CD pipelines, and why behavioral analysis helps detect threats signatures miss.',
    },
    {
        slug: slugSearchLeak,
        titleEn: TK.searchLeak,
        date: 'July 13th 2026',
        coverImage: '/images/blog/blog18.png',
        seoTitle: 'SearchLeak CVE-2026-42824: Microsoft 365 Copilot Flaw',
        seoDescription:
            'SearchLeak (CVE-2026-42824) let trusted Microsoft 365 links trigger one-click data theft via Copilot. See how it worked and what Microsoft fixed.',
    },
    {
        slug: slugJadePuffer,
        titleEn: TK.jadePuffer,
        date: 'July 13th 2026',
        coverImage: '/images/blog/blog19.png',
        seoTitle: 'JadePuffer: First Agentic Ransomware Attack Chain',
        seoDescription:
            'JadePuffer used AI to exploit Langflow’s CVE-2025-3248, harvest credentials, and encrypt 1,342 Nacos records. See the attack chain and fixes.',
    },
    {
        slug: slugAiAgentSandboxEscapes,
        titleEn: TK.aiAgentSandboxEscapes,
        date: 'July 31st 2026',
        coverImage: '/images/blog/blog20.png',
        seoTitle: 'OpenAI – Hugging Face Breach and Claude Cowork SharedRoot',
        seoDescription:
            'See how an OpenAI agent breached Hugging Face and how Claude Cowork’s SharedRoot flaw exposed critical weaknesses in AI agent sandbox security.',
    },
    {
        slug: slugAncpiRansomware,
        titleEn: TK.ancpiRansomware,
        date: 'August 18th 2026',
        coverImage: '/images/blog/blog21.png',
        seoTitle: 'ANCPI Ransomware Attack 2026: The ByteToBreach Breach',
        seoDescription:
            'How ByteToBreach turned one exposed login page into control of 1,083 virtual machines at Romania’s land registry, and what the DNSC report found.',
    },
    {
        slug: slugFakeClaudeDesktop,
        titleEn: TK.fakeClaudeDesktop,
        date: 'September 30th 2026',
        coverImage: '/images/blog/blog22.png',
        seoTitle: 'Fake Claude Desktop Malware Delivers SectopRAT Through DLL Sideloading',
        seoDescription:
            'Fake Claude Desktop malware used Bing ads, DLL sideloading, GPU evasion, and blockchain C2 to deliver SectopRAT. Review IOCs and detection guidance.',
    },
];

export const BLOG_INDEX: BlogPostMeta[] = sortBlogByDateDesc(BLOG_ENTRIES);

export const getBlogMetaBySlug = (slug: string): BlogPostMeta | undefined => BLOG_INDEX.find((p) => p.slug === slug);

/**
 * Stable, language-independent OG image per blog slug. Used by social
 * crawlers (LinkedIn/Facebook/X) that fetch the page server-side and need a
 * deterministic absolute URL.
 */
export const getImageForPostSlug = (slug: string, siteUrl: string): string => {
    const path = (() => {
        if (slug.includes('free-url') || slug.includes('phishing')) {
            return '/images/blog/Free-URL-Link-Scanners-1.png';
        }
        if (slug.includes('cloud-files-at-risk') || slug.includes('what-100k-file-scans')) {
            return '/images/blog/are-your-cloud-files-secure-1.jpg';
        }
        if (slug.includes('building-a-safer-internet') || slug.includes('metadefender-cloud-community-a-safe')) {
            return '/images/blog/building-a-safer-internet-1.png';
        }
        if (slug.includes('multi-engine') || slug.includes('isnt-enough')) {
            return '/images/blog/infected-scan.png';
        }
        if (slug.includes('why-file-security-in-transit')) {
            return '/images/blog/file-security-transit-1.png';
        }
        if (slug.includes('secure-your-storage-data')) {
            return '/images/blog/storage-data-1.png';
        }
        const meta = getBlogMetaBySlug(slug);
        if (meta?.coverImage) {
            return meta.coverImage;
        }
        return '/images/blog/homepage.png';
    })();
    return `${siteUrl}${path}`;
};
